import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheck, Zap, Download, Infinity as InfinityIcon, RefreshCw } from 'lucide-react';
type ModalType = 'terms' | 'privacy' | 'contact' | null;
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-259">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-260">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-261" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25
      }} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-262">
            <div className="flex items-center justify-between p-6 border-b border-brand-beige" data-id="element-263">
              <h3 className="text-xl md:text-2xl font-bold text-brand-green font-serif" data-id="element-264">
                {title}
              </h3>
              <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full bg-brand-beige hover:bg-brand-beige/80 transition-colors" data-id="element-265">
                <X className="w-5 h-5 text-brand-text" data-id="element-266" />
              </button>
            </div>
            <div className="p-6 md:p-8 overflow-y-auto max-h-[calc(85vh-80px)] text-brand-text/80 text-sm md:text-base leading-relaxed space-y-4" data-id="element-267">
              {children}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-brand-green text-white relative overflow-hidden" data-id="element-268">
        {/* Decorative background */}
        <div className="absolute inset-0 overflow-hidden opacity-10 pointer-events-none" data-id="element-269">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-brand-gold rounded-full blur-3xl" data-id="element-270"></div>
          <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] bg-brand-gold rounded-full blur-3xl" data-id="element-271"></div>
        </div>

        {/* Closing quote */}
        <div className="relative z-10 border-b border-white/10" data-id="element-272">
          <div className="max-w-4xl mx-auto px-6 py-12 md:py-14 text-center" data-id="element-273">
            <p className="font-serif text-xl sm:text-2xl md:text-3xl text-white/90 leading-snug italic tracking-tight" data-id="element-274">
              &ldquo;No es solo vender online. Es construir una{' '}
              <span className="text-brand-gold not-italic font-semibold" data-id="element-275">
                oportunidad real
              </span>{' '}
              para tu futuro.&rdquo;
            </p>
          </div>
        </div>

        {/* Main footer content */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-16 md:py-20" data-id="element-276">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-10 text-center md:text-left" data-id="element-277">
            {/* Block 1: Brand */}
            <div data-id="element-278">
              <h4 className="font-serif text-lg font-bold tracking-wide mb-3 text-brand-gold" data-id="element-279">
                ECOSISTEMA ECOMMERCE
              </h4>
              <p className="text-white/55 text-sm leading-relaxed max-w-xs mx-auto md:mx-0" data-id="element-280">
                Aprende Ecommerce moderno y Amazon con sistemas prácticos
                diseñados para ayudarte a construir ingresos online reales.
              </p>
            </div>

            {/* Block 2: Legal links */}
            <div data-id="element-281">
              <h5 className="text-xs font-bold uppercase tracking-[0.18em] text-brand-gold mb-4" data-id="element-282">
                Legal
              </h5>
              <div className="flex flex-col gap-3" data-id="element-283">
                <button onClick={() => setActiveModal('terms')} className="text-white/60 text-sm hover:text-brand-gold transition-colors text-center md:text-left" data-id="element-284">
                  Términos y Condiciones
                </button>
                <button onClick={() => setActiveModal('privacy')} className="text-white/60 text-sm hover:text-brand-gold transition-colors text-center md:text-left" data-id="element-285">
                  Política de Privacidad
                </button>
              </div>
            </div>

            {/* Block 3: Trust */}
            <div data-id="element-286">
              <h5 className="text-xs font-bold uppercase tracking-[0.18em] text-brand-gold mb-4" data-id="element-287">
                Tu acceso
              </h5>
              <div className="flex flex-col gap-3" data-id="element-288">
                <span className="flex items-center justify-center md:justify-start gap-2 text-white/65 text-sm" data-id="element-289">
                  <ShieldCheck className="w-4 h-4 text-brand-gold flex-shrink-0" data-id="element-290" />
                  Compra Segura
                </span>
                <span className="flex items-center justify-center md:justify-start gap-2 text-white/65 text-sm" data-id="element-291">
                  <Zap className="w-4 h-4 text-brand-gold flex-shrink-0" data-id="element-292" />
                  Acceso Inmediato
                </span>
                <span className="flex items-center justify-center md:justify-start gap-2 text-white/65 text-sm" data-id="element-293">
                  <Download className="w-4 h-4 text-brand-gold flex-shrink-0" data-id="element-294" />
                  Descarga Digital
                </span>
                <span className="flex items-center justify-center md:justify-start gap-2 text-white/65 text-sm" data-id="element-295">
                  <InfinityIcon className="w-4 h-4 text-brand-gold flex-shrink-0" data-id="element-296" />
                  Acceso de por vida
                </span>
                <span className="flex items-center justify-center md:justify-start gap-2 text-white/65 text-sm" data-id="element-297">
                  <RefreshCw className="w-4 h-4 text-brand-gold flex-shrink-0" data-id="element-298" />
                  Actualizaciones gratuitas
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Digital products legal note */}
        <div className="relative z-10 border-t border-white/10" data-id="element-299">
          <div className="max-w-4xl mx-auto px-6 py-8 md:py-10" data-id="element-300">
            <div className="bg-white/5 border border-white/10 rounded-2xl px-6 py-6" data-id="element-301">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-gold mb-3 text-center" data-id="element-302">
                Política de productos digitales
              </p>
              <p className="text-white/55 text-xs sm:text-[13px] leading-relaxed mb-3" data-id="element-303">
                Al realizar esta compra, el cliente reconoce que está
                adquiriendo un producto digital descargable de acceso inmediato.
                Debido a la naturaleza intangible del contenido digital, todas
                las ventas son finales y no aplican devoluciones ni reembolsos
                una vez enviado el acceso al producto.
              </p>
              <p className="text-white/45 text-xs sm:text-[13px] leading-relaxed" data-id="element-304">
                Digital Products NC se compromete únicamente a garantizar la
                correcta entrega del contenido adquirido y la asistencia en caso
                de problemas técnicos relacionados con la descarga o recepción
                del material.
              </p>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="relative z-10 border-t border-white/10" data-id="element-305">
          <div className="max-w-6xl mx-auto px-6 py-6 text-center" data-id="element-306">
            {/* Copyright */}
            <p className="text-white/40 text-xs mb-3" data-id="element-307">
              © {new Date().getFullYear()} Ecosistema Ecommerce. Todos los
              derechos reservados.
            </p>
            {/* Disclaimer */}
            <p className="text-white/25 text-[10px] max-w-2xl mx-auto leading-relaxed" data-id="element-308">
              Este sitio no es parte del sitio web de Facebook o Facebook Inc.
              Además, este sitio NO está respaldado por Facebook de ninguna
              manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
            </p>
          </div>
        </div>
      </footer>

      {/* Terms Modal */}
      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-309">
        <p className="font-semibold text-brand-text" data-id="element-310">
          Última actualización: {new Date().getFullYear()}
        </p>
        <p data-id="element-311">
          Al adquirir cualquier producto digital ofrecido a través de este sitio
          web, aceptas los siguientes términos y condiciones:
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-312">
          1. Naturaleza del Producto
        </h4>
        <p data-id="element-313">
          Todos los productos ofrecidos son de naturaleza digital (ebooks,
          plantillas, checklists). Una vez completada la compra, recibirás
          acceso inmediato al contenido a través de descarga digital.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-314">2. Uso Personal</h4>
        <p data-id="element-315">
          El contenido adquirido es para uso personal y educativo únicamente.
          Queda estrictamente prohibida la redistribución, reventa, copia o
          compartición del material con terceros sin autorización expresa por
          escrito.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-316">
          3. Política de No Reembolso
        </h4>
        <p data-id="element-317">
          Debido a la naturaleza digital e inmediata del producto, no se ofrecen
          reembolsos una vez que el contenido ha sido entregado y/o descargado.
          Al realizar la compra, aceptas esta condición.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-318">
          4. Propiedad Intelectual
        </h4>
        <p data-id="element-319">
          Todo el contenido, diseño, textos, imágenes y materiales están
          protegidos por derechos de autor. La propiedad intelectual pertenece
          exclusivamente a Sistema de Ingreso Desde Casa.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-320">5. Modificaciones</h4>
        <p data-id="element-321">
          Nos reservamos el derecho de modificar el contenido, precios, ofertas
          y estos términos en cualquier momento sin previo aviso. Las compras
          realizadas antes de cualquier cambio mantienen las condiciones
          vigentes al momento de la transacción.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-322">6. Resultados</h4>
        <p data-id="element-323">
          Los resultados pueden variar según la dedicación, esfuerzo y
          circunstancias individuales de cada persona. No garantizamos ingresos
          específicos ni resultados financieros.
        </p>
      </Modal>

      {/* Privacy Modal */}
      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-324">
        <p className="font-semibold text-brand-text" data-id="element-325">
          Última actualización: {new Date().getFullYear()}
        </p>
        <p data-id="element-326">
          Tu privacidad es importante para nosotros. Esta política describe cómo
          recopilamos, usamos y protegemos tu información personal.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-327">
          1. Información que Recopilamos
        </h4>
        <p data-id="element-328">
          Recopilamos información que proporcionas voluntariamente al realizar
          una compra, como tu nombre, dirección de correo electrónico e
          información de pago. Esta información es necesaria para procesar tu
          pedido y entregar el producto.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-329">
          2. Uso de la Información
        </h4>
        <p data-id="element-330">Tu información se utiliza exclusivamente para:</p>
        <ul className="list-disc pl-6 space-y-1" data-id="element-331">
          <li data-id="element-332">Procesar y entregar tu compra</li>
          <li data-id="element-333">Enviarte el producto adquirido</li>
          <li data-id="element-334">Comunicarnos contigo sobre tu pedido</li>
          <li data-id="element-335">
            Enviarte información relevante sobre actualizaciones del producto
            (si lo autorizas)
          </li>
        </ul>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-336">
          3. Protección de Datos
        </h4>
        <p data-id="element-337">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger tu información personal contra acceso no autorizado, pérdida
          o alteración.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-338">
          4. No Venta de Datos
        </h4>
        <p data-id="element-339">
          No vendemos, alquilamos ni compartimos tu información personal con
          terceros con fines comerciales. Tu información es confidencial y está
          protegida.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-340">
          5. Cookies y Analíticas
        </h4>
        <p data-id="element-341">
          Este sitio puede utilizar cookies y herramientas de analítica web para
          mejorar la experiencia del usuario y analizar el tráfico. Estas
          herramientas recopilan información de forma anónima.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-342">6. Tus Derechos</h4>
        <p data-id="element-343">
          Tienes derecho a solicitar acceso, corrección o eliminación de tus
          datos personales en cualquier momento contactándonos a través de
          nuestro correo de soporte.
        </p>
      </Modal>

      {/* Contact Modal */}
      <Modal isOpen={activeModal === 'contact'} onClose={() => setActiveModal(null)} title="Contacto" data-id="element-344">
        <p className="text-lg text-brand-text" data-id="element-345">
          Si tienes dudas, preguntas o necesitas soporte con tu compra, puedes
          escribirnos directamente.
        </p>
        <div className="bg-brand-beige/50 rounded-xl p-6 space-y-4 mt-4" data-id="element-346">
          <div data-id="element-347">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-text/50 mb-1" data-id="element-348">
              Correo electrónico
            </p>
            <p className="text-lg font-semibold text-brand-green" data-id="element-349">
              productodigitalnc@gmail.com
            </p>
          </div>
          <div data-id="element-350">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-text/50 mb-1" data-id="element-351">
              Horario de atención
            </p>
            <p className="text-brand-text font-medium" data-id="element-352">
              Lunes a Viernes | 9:00 AM - 6:00 PM EST
            </p>
          </div>
          <div data-id="element-353">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-text/50 mb-1" data-id="element-354">
              Tiempo estimado de respuesta
            </p>
            <p className="text-brand-text font-medium" data-id="element-355">24 - 48 horas hábiles</p>
          </div>
        </div>
        <p className="text-brand-text/60 text-sm mt-4" data-id="element-356">
          Estamos aquí para ayudarte. Tu satisfacción es nuestra prioridad.
        </p>
      </Modal>
    </>;
}